import { useEffect, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { useWorkspace } from '@/components/layout/workspace/workspaceContext';
import {
  Badge,
  Button,
  ConversationFeed,
  Dialog,
  DialogContent,
  DialogHeader,
  MentionsInbox,
  Message,
  ResponseTimer,
  ReplyComposer,
} from '@/components/ui';
import type { InboxFilter, InboxSort, InboxThread } from '@/lib/mentionsInbox';
import { DEFAULT_INBOX_FILTER } from '@/lib/mentionsInbox';
import { groupInbox } from '@/lib/mentionsInbox';
import { DEMO_AUTHOR, ENTITIES, HOME } from './model';
import { makeInboxEntries, RESPONSE_POLICY, AGE_OPTIONS } from './inboxModel';
import { useInboxSample } from './inboxSampleContext';
import { useSampleMessageActions } from './useSampleMessageActions';
import { REPLY_TEMPLATES, replyVariables } from './personModel';
import type { ReplyVariable } from '@/lib/replyTemplates';

export function InboxDemo() {
  const actions = useSampleMessageActions();
  const ws = useWorkspace();
  const { entries, setEntries } = useInboxSample();
  const [filter, setFilter] = useState<InboxFilter>(DEFAULT_INBOX_FILTER);
  const [sort, setSort] = useState<InboxSort>('recent');
  const [density, setDensity] = useState<'comfortable' | 'compact'>('comfortable');
  const [state, setState] = useState<'ready' | 'loading' | 'error'>('ready');
  const [empty, setEmpty] = useState(false);
  const [reply, setReply] = useState<InboxThread | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [variableSets, setVariableSets] = useState<Record<string, ReplyVariable[]>>({});
  const variables = reply
    ? (variableSets[reply.key] ??
      replyVariables(reply.latest.message.author.name.split(' ')[0], reply.subject))
    : [];
  const [notice, setNotice] = useState('');
  const trigger = useRef<HTMLButtonElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const sequence = useRef(0);
  useEffect(() => () => clearTimeout(timer.current), []);
  function reload() {
    clearTimeout(timer.current);
    setState('loading');
    timer.current = setTimeout(() => setState('ready'), 1600);
  }
  function openReply(thread: InboxThread, from: HTMLButtonElement) {
    trigger.current = from;
    setReply(thread);
  }
  function sendReply(text: string) {
    if (!reply || !text.trim()) return;
    const latest = reply.latest;
    setEntries((current) => [
      ...current.map((entry) =>
        reply.entries.some((item) => item.id === entry.id)
          ? { ...entry, unread: false, message: { ...entry.message, response: undefined } }
          : entry,
      ),
      {
        ...latest,
        id: 'demo-reply-' + ++sequence.current,
        unread: false,
        mine: true,
        message: {
          id: 'demo-reply-' + sequence.current,
          author: DEMO_AUTHOR,
          sentAt: new Date().toISOString(),
          source: latest.message.source,
          content: { format: 'plain', text },
        },
      },
    ]);
    setDrafts((current) => ({ ...current, [reply.key]: '' }));
    setNotice('Ответ сохранён в образце: «' + reply.subject + '».');
    setReply(null);
  }
  return (
    <section aria-label="Образец единого ящика" className="min-w-0">
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-line bg-muted p-3">
        <span className="type-overline text-fg-muted">Состояния образца</span>
        <Button variant="ghost" size="sm" onClick={reload}>
          Загрузка ящика
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            clearTimeout(timer.current);
            setState('error');
          }}
        >
          Ошибка обновления
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setEmpty(!empty)}>
          {empty ? 'Вернуть обращения' : 'Пустой ящик'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setEntries(makeInboxEntries(Date.now()));
            setFilter(DEFAULT_INBOX_FILTER);
            setSort('recent');
            setState('ready');
            setEmpty(false);
            setNotice('Образец восстановлен.');
          }}
        >
          <RotateCcw className="size-3.5" />
          Сбросить образец
        </Button>
      </div>
      <p className="mb-4 text-xs text-fg-muted">
        Время МСК. Пример порогов: от 1 часа — внимание, от 2 часов — срочно. У каждого сервиса могут быть
        свои сроки.
      </p>
      <p role="status" className="mb-3 text-sm text-success-fg">
        {notice}
      </p>
      <MentionsInbox
        entries={empty ? [] : entries}
        filter={filter}
        onFilterChange={setFilter}
        sort={sort}
        onSortChange={setSort}
        ageOptions={AGE_OPTIONS}
        density={density}
        onDensityChange={setDensity}
        state={state}
        onRetry={reload}
        {...actions}
        onOpenSource={(thread) => {
          setEntries((current) =>
            current.map((entry) =>
              thread.entries.some((item) => item.id === entry.id) ? { ...entry, unread: false } : entry,
            ),
          );
          ws.go(HOME + '/source/' + thread.entries[0].id);
        }}
        onReply={openReply}
      />
      <Dialog
        open={!!reply}
        onOpenChange={(open) => {
          if (!open) setReply(null);
        }}
      >
        <DialogContent
          size="lg"
          className="max-h-full overflow-y-auto"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (trigger.current?.isConnected) trigger.current.focus();
            else document.querySelector<HTMLElement>('[data-inbox-focus]')?.focus();
          }}
        >
          <DialogHeader
            title={'Ответить: ' + (reply?.subject ?? '')}
            description="Ответ останется только в этом образце."
          />
          {reply && (
            <div className="space-y-4 px-6 pb-6">
              <div className="rounded-md border border-line bg-card">
                <Message message={reply.latest.message} entities={ENTITIES} />
              </div>
              <ReplyComposer
                value={drafts[reply.key] ?? ''}
                onValueChange={(value) => setDrafts((current) => ({ ...current, [reply.key]: value }))}
                templates={REPLY_TEMPLATES}
                variables={variables}
                onVariableChange={(key, value) =>
                  setVariableSets((current) => ({
                    ...current,
                    [reply.key]: variables.map((item) => (item.key === key ? { ...item, value } : item)),
                  }))
                }
                onSubmit={sendReply}
                submitLabel="Сохранить ответ в образце"
              />
              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={() => setReply(null)}>
                  Закрыть
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

export function InboxSource({ entryId }: { entryId: string }) {
  const actions = useSampleMessageActions();
  const { entries } = useInboxSample();
  const thread = groupInbox(entries).find((group) => group.entries.some((entry) => entry.id === entryId));
  const ws = useWorkspace();
  return (
    <article className="mx-auto max-w-5xl space-y-6 p-6">
      <header>
        <Badge>Вымышленный источник</Badge>
        <h2 className="mt-3 type-title">{thread?.subject ?? 'Ветка не найдена'}</h2>
        <p className="mt-2 text-sm text-fg-muted">
          Так выглядит переход из ящика к ветке. Подключение настоящего источника задаёт сервис.
        </p>
        <Button variant="link" size="sm" onClick={() => ws.go(HOME)}>
          Вернуться к ящику
        </Button>
      </header>
      {thread && (
        <div className="overflow-hidden rounded-lg border border-line bg-card">
          <ConversationFeed>
            {thread.entries.map((entry) => (
              <li key={entry.id}>
                <Message message={entry.message} {...actions} />
              </li>
            ))}
          </ConversationFeed>
        </div>
      )}
    </article>
  );
}

export function ResponseTimeDemo() {
  const [base] = useState(() => Date.now());
  const [shift, setShift] = useState(0);
  const now = base + shift;
  return (
    <section aria-label="Срок ответа" className="space-y-4">
      <header>
        <h3 className="type-heading">Срок ответа</h3>
        <p className="mt-1 text-sm text-fg-muted">
          Один таймер для сообщения и ящика. Времена и пороги — вымышленные.
        </p>
      </header>
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="secondary" size="sm" onClick={() => setShift((value) => value + 60_000)}>
          В образце: +1 минута
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setShift(0)}>
          Сначала
        </Button>
        <p className="text-xs text-fg-muted">Сдвиг времени: {shift / 60_000} мин</p>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {[59, 119, 1560].map((minutes) => (
          <div key={minutes} className="rounded-lg border border-line bg-card p-4">
            <p className="mb-3 type-overline text-fg-muted">
              {minutes === 59
                ? 'Рядом с первым порогом'
                : minutes === 119
                  ? 'Рядом со вторым порогом'
                  : 'Долгое ожидание'}
            </p>
            <ResponseTimer
              wait={{ since: new Date(base - minutes * 60_000).toISOString(), thresholds: RESPONSE_POLICY }}
              now={now}
            />
          </div>
        ))}
      </div>
      <div className="overflow-hidden rounded-lg border border-line bg-card">
        <Message
          responseNow={now}
          message={{
            id: 'response-example',
            author: ENTITIES[0],
            sentAt: new Date(base - 119 * 60_000).toISOString(),
            content: {
              format: 'plain',
              text: 'Посмотри, пожалуйста, обновлённый вариант. Жду твоего ответа.',
            },
            response: { since: new Date(base - 119 * 60_000).toISOString(), thresholds: RESPONSE_POLICY },
          }}
        />
      </div>
      <p className="text-xs text-fg-muted">
        В ящике время идёт само. Здесь кнопка помогает рассмотреть переход через пороги без ожидания.
      </p>
    </section>
  );
}
